import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { fileUrl } from '../api';
import AdminExpensesTab from '../components/AdminExpensesTab';
import './admin.css';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: 'super_admin' | 'partner' | 'team_member' | string;
  reimbursement_currency: string;
  status?: 'active' | 'inactive' | string;
  active?: boolean; // legacy
  created_at: string;
}

interface Expense {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  type: string;
  justification: string;
  amount: number;
  currency: string;
  reimbursement_amount: number;
  reimbursement_currency: string;
  exchange_rate: number;
  override_exchange_rate?: number;
  override_reason?: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  receipt: string | null;
  admin_notes: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  created_at: string;
  unique_id: string;
  // Optional extended fields
  classification?: string;
  vat_amount?: number | null;
  no_receipt?: number | boolean | null;
}

interface Report {
  id: number;
  filename: string;
  user_name: string;
  user_id: number;
  generated_at: string;
  expense_count: number;
  total_amount: number;
  currency: string;
  expense_ids?: string;
  pdf_filename?: string | null;
  // Optional SharePoint Online URLs provided by backend enrichment
  sp_xls_web_url?: string | null;
  sp_zip_web_url?: string | null;
  sp_folder_web_url?: string | null;
}

type Invite = {
  id: number;
  email: string;
  token: string;
  role: string;
  expires_at: string | null;
  used_at: string | null;
  created_at: string;
};

const Admin = () => {
  const [activeTab, setActiveTab] = useState('expenses');
  const [users, setUsers] = useState([] as User[]);
  const [expenses, setExpenses] = useState([] as Expense[]);
  const [reports, setReports] = useState([] as Report[]);
  const [reportUserFilter, setReportUserFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success' as 'success' | 'error');
  
  // Expense filtering
  const [userFilter, setUserFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selected expenses for bulk actions
  const [selectedExpenses, setSelectedExpenses] = useState([] as number[]);
  
  // Modals
  const [selectedExpense, setSelectedExpense] = useState(null as Expense | null);
  const [selectedUser, setSelectedUser] = useState(null as User | null);
  const [selectedReport, setSelectedReport] = useState(null as Report | null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [viewingReport, setViewingReport] = useState(null as Report | null);
  const [reportExpenses, setReportExpenses] = useState([] as Expense[]);
  const [excelPreviewHeaders, setExcelPreviewHeaders] = useState([] as string[]);
  const [excelPreviewRows, setExcelPreviewRows] = useState([] as any[]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState(null as string | null);
  
  // Receipt preview
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [selectedExpenseForPreview, setSelectedExpenseForPreview] = useState(null as Expense | null);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState('');
  // Admin-generated password reset code modal
  const [showResetCodeModal, setShowResetCodeModal] = useState(false);
  const [resetCodeLoading, setResetCodeLoading] = useState(false);
  const [resetCodeError, setResetCodeError] = useState('');
  type ResetCodeData = {
    user?: Pick<User, 'id' | 'email' | 'first_name' | 'last_name'>;
    code?: string;
    expiresAt?: string; // computed client-side from expires_in_seconds
  };
  const [resetCodeData, setResetCodeData] = useState({} as ResetCodeData);

  const token = localStorage.getItem('token');

  // Users tab state
  const [usersLoading, setUsersLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null as number | null);
  const [editDraft, setEditDraft] = useState({} as Partial<User>);
  // Invite creation state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('team_member' as 'team_member' | 'partner' | 'super_admin');
  const [inviteDays, setInviteDays] = useState(14);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteResult, setInviteResult] = useState(null as { token: string; email: string; role: string; expires_at: string } | null);
  const [inviteHistory, setInviteHistory] = useState<Invite[]>([]);

  const tabs = [
    { key: 'expenses', label: 'Expenses', icon: '💳' },
    { key: 'reports', label: 'Reports', icon: '📑' },
    { key: 'users', label: 'Users', icon: '👥' },
    { key: 'analytics', label: 'Analytics', icon: '📈' }
  ];

  const fetchInvites = async () => {
    try {
      const resp = await api.get('/api/admin/invites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteHistory(resp.data || []);
    } catch (e) {
      console.error('Failed to fetch invites:', e);
    }
  };

  const startEditUser = (u: User) => {
    setEditingUserId(u.id);
    setEditDraft({ ...u });
  };

  const generateResetCode = async (u: User) => {
    setResetCodeError('');
    setShowResetCodeModal(false);
    setResetCodeLoading(true);
    try {
      const resp = await api.post(
        `/api/admin/users/${u.id}/reset-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { reset_code, expires_in_seconds } = resp.data || {};
      const expiresAt = new Date(Date.now() + (Number(expires_in_seconds || 3600) * 1000)).toISOString();
      setResetCodeData({
        user: { id: u.id, email: u.email, first_name: u.first_name, last_name: u.last_name },
        code: String(reset_code || ''),
        expiresAt
      });
      setShowResetCodeModal(true);
    } catch (e: any) {
      console.error('Failed to generate reset code', e);
      setResetCodeError(e?.response?.data?.message || 'Failed to generate reset code');
      setShowResetCodeModal(true);
    } finally {
      setResetCodeLoading(false);
    }
  };

  const createInvite = async () => {
    setInviteError('');
    setInviteResult(null);
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }
    try {
      setInviteLoading(true);
      const resp = await api.post('/api/auth/invites', {
        email: inviteEmail.trim(),
        role: inviteRole,
        expires_in_days: inviteDays
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInviteResult(resp.data);
      await fetchInvites();
    } catch (e: any) {
      setInviteError(e?.response?.data?.message || 'Failed to create invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const cancelEditUser = () => {
    setEditingUserId(null);
    setEditDraft({});
  };

  const saveUser = async (u: User) => {
    // Enforce at least one super admin
    const currentSupers = users.filter(x => x.role === 'super_admin');
    const draftRole = (editDraft.role as any) || u.role;
    const demotingLastSuper = (u.role === 'super_admin' && draftRole !== 'super_admin' && currentSupers.length === 1);
    if (demotingLastSuper) {
      showMessage('There must be at least one super admin.', 'error');
      return;
    }
    try {
      const body = {
        role: draftRole,
        status: (editDraft.status as any) || u.status,
        first_name: (editDraft.first_name as any) ?? u.first_name ?? '',
        last_name: (editDraft.last_name as any) ?? u.last_name ?? '',
        reimbursement_currency: (editDraft.reimbursement_currency as any) ?? u.reimbursement_currency
      };
      await api.put(`/api/admin/users/${u.id}/role`, body, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      showMessage('User updated', 'success');
      setEditingUserId(null);
      setEditDraft({});
      fetchUsers();
    } catch (e: any) {
      console.error('Failed to update user', e);
      showMessage(e?.response?.data?.message || 'Update failed', 'error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'reports') {
        fetchReports();
        fetchInvites();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      await Promise.all([
        fetchUsers(),
        fetchExpenses(),
        fetchInvites()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showMessage('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.get('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      if (error?.response?.status === 403) {
        setAccessDenied(true);
        showMessage('Access denied. Only Super Admins can view admin data.', 'error');
      }
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchExpenses = async (page = 0) => {
    try {
      const limit = 100;
      const offset = page * limit;
      
      const response = await api.get(`/api/admin/expenses-with-users?limit=${limit}&offset=${offset}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data;
      
      // Handle new paginated response format
      if (data.expenses && data.pagination) {
        // Normalize numeric fields
        const normalized = data.expenses.map((e: any) => ({
          ...e,
          amount: e.amount !== null && e.amount !== undefined ? Number(e.amount) : e.amount,
          reimbursement_amount: e.reimbursement_amount !== null && e.reimbursement_amount !== undefined ? Number(e.reimbursement_amount) : e.reimbursement_amount,
          exchange_rate: e.exchange_rate !== null && e.exchange_rate !== undefined ? Number(e.exchange_rate) : e.exchange_rate,
          vat_amount: e.vat_amount !== null && e.vat_amount !== undefined && e.vat_amount !== '' ? Number(e.vat_amount) : null
        }));
        
        setExpenses(normalized as Expense[]);
        
        // Store pagination info and summary in state (you'll need to add these state variables)
        // For now, we'll just log them
        console.log('Pagination:', data.pagination);
        console.log('Summary:', data.summary);
      } else {
        // Fallback for old response format (backward compatibility)
        const raw = (data || []) as any[];
        const normalized = raw.map((e: any) => ({
          ...e,
          amount: e.amount !== null && e.amount !== undefined ? Number(e.amount) : e.amount,
          reimbursement_amount: e.reimbursement_amount !== null && e.reimbursement_amount !== undefined ? Number(e.reimbursement_amount) : e.reimbursement_amount,
          exchange_rate: e.exchange_rate !== null && e.exchange_rate !== undefined ? Number(e.exchange_rate) : e.exchange_rate,
          vat_amount: e.vat_amount !== null && e.vat_amount !== undefined && e.vat_amount !== '' ? Number(e.vat_amount) : null
        }));
        setExpenses(normalized as Expense[]);
      }
    } catch (error: any) {
      console.error('Failed to fetch expenses:', error);
      console.error('Error details:', error.response?.data);
      if (error?.response?.status === 403) {
        setAccessDenied(true);
        showMessage('Access denied. Only Super Admins can view admin data.', 'error');
      } else {
        showMessage(`Failed to fetch expenses: ${error.response?.data?.message || error.message}`, 'error');
      }
      setExpenses([]);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await api.get('/api/admin/reports', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setReports(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch reports:', error);
      if (error?.response?.status === 403) {
        setAccessDenied(true);
        showMessage('Access denied. Only Super Admins can view admin data.', 'error');
      }
      setReports([]);
    }
  };

  const showMessage = (msg: string, type: 'success' | 'error' = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  };

  const formatCurrency = (amount: number | null | undefined, currency: string = '') => {
    if (amount === null || amount === undefined || isNaN(amount)) return 'N/A';
    // Format number with 2 decimal places and append 3-letter currency code
    const formattedAmount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return currency ? `${formattedAmount} ${currency}` : `${formattedAmount}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Reports helpers
  const filteredReports = reports.filter(r => !reportUserFilter || r.user_name === reportUserFilter);

  const parseExpenseIds = (raw: string | null | undefined): string[] => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x).trim()).filter(Boolean);
    } catch {}
    return raw.split(',').map((id) => id.trim()).filter(Boolean);
  };

  // Prefer reimbursement currency from any expense within the report
  const getReportReimbursementCurrency = (r: Report): string => {
    const ids = parseExpenseIds(r.expense_ids || '');
    if (ids.length > 0) {
      const match = expenses.find((e: any) => {
        const candidateIds = [e.expense_id, e.unique_id, e.id].map((v: any) => (v !== undefined && v !== null) ? String(v) : '');
        return candidateIds.some((cid: string) => cid && ids.includes(cid));
      });
      if (match && match.reimbursement_currency) return match.reimbursement_currency;
    }
    // Fallback to user's reimbursement currency by user_id
    const userCur = users.find(u => Number(u.id) === Number(r.user_id))?.reimbursement_currency;
    if (userCur) return userCur;
    // Additional fallback: try match by full name (legacy reports)
    const byName = users.find(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() === (r.user_name || '').trim());
    if (byName && byName.reimbursement_currency) return byName.reimbursement_currency;
    // Last resort: report-level currency (may be legacy)
    return r.currency || '';
  };

  const openReportModal = (report: Report) => {
    try {
      const ids = parseExpenseIds(report.expense_ids || '');
      // Filter from already-fetched admin expenses; match various id fields
      const matched = expenses.filter((e: any) => {
        const isPaid = e.status === 'paid';
        const candidateIds = [e.expense_id, e.unique_id, e.id].map((v) => (v !== undefined && v !== null) ? String(v) : '');
        const matches = candidateIds.some((cid) => cid && ids.includes(cid));
        return isPaid && matches;
      });
      setReportExpenses(matched as any);
    } catch {
      setReportExpenses([]);
    }
    setViewingReport(report);
    setShowReportModal(true);
    setExcelPreviewHeaders([]);
    setExcelPreviewRows([]);
    setExcelError(null);
  };

  const loadExcelPreview = async (report: Report) => {
    setExcelLoading(true);
    setExcelError(null);
    try {
      const resp = await api.get(
        `/api/admin/reports/${encodeURIComponent(report.filename)}/json`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const headers: string[] = resp.data?.headers || [];
      const rows: any[][] = resp.data?.rows || [];
      setExcelPreviewHeaders(headers);
      setExcelPreviewRows(rows);
    } catch (err: any) {
      console.error('Failed to load report preview (JSON):', err);
      setExcelError('Failed to load report preview. You can still download the file.');
    } finally {
      setExcelLoading(false);
    }
  };

  const handleDownloadReceiptsZip = async (filename: string) => {
    try {
      const response = await api.get(`/api/admin/reports/${encodeURIComponent(filename)}/receipts-zip`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const datePart = (filename.match(/(\d{4}-\d{2}-\d{2})/)?.[1]) || 'receipts';
      a.download = filename.replace('.xlsx', `_receipts_${datePart}.zip`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download receipts ZIP:', err);
      showMessage('Failed to download receipts ZIP', 'error');
    }
  };

  const downloadReportAs = async (report: Report, kind: 'download' | 'pdf') => {
    const isPdf = kind === 'pdf';
    try {
      const response = await api.get(`/api/admin/reports/${encodeURIComponent(report.filename)}/${kind}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], isPdf ? { type: 'application/pdf' } : undefined));
      const a = document.createElement('a');
      a.href = url;
      a.download = isPdf ? (report.pdf_filename || report.filename.replace(/\.xlsx$/, '.pdf')) : report.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Failed to download ${isPdf ? 'PDF' : 'Excel'} report:`, err);
      showMessage(`Failed to download ${isPdf ? 'PDF' : 'Excel'} report`, 'error');
    }
  };
  const handleDownloadPdf = (report: Report) => downloadReportAs(report, 'pdf');
  const handleDownloadExcel = (report: Report) => downloadReportAs(report, 'download');

  const handleExpenseAction = async (expenseId: number, action: 'approve' | 'reject' | 'unapprove') => {
    try {
      await api.put(`/api/admin/expenses/${expenseId}/${action}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      showMessage(`Expense ${action}d successfully!`);
      setSelectedExpense(null);
      fetchExpenses();
    } catch (error) {
      console.error(`Failed to ${action} expense:`, error);
      showMessage(`Failed to ${action} expense`, 'error');
    }
  };

  if (accessDenied) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          maxWidth: '1000px',
          margin: '40px auto',
          backgroundColor: 'white',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              backgroundColor: '#fee2e2', color: '#b91c1c',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700
            }}>!</div>
            <div>
              <h3 style={{ margin: 0, color: '#b91c1c' }}>Access denied</h3>
              <p style={{ margin: '6px 0 0 0', color: '#7f1d1d' }}>
                Only Super Admins can view the Admin panel data. Please log in with a super admin account.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1 className="admin-page__title">Admin Panel</h1>
        <p className="admin-page__subtitle">Manage users, approve expenses, and view analytics</p>
      </div>

      <div className="admin-page__content">
        {accessDenied && (
          <div style={{
            maxWidth: '1000px',
            margin: '40px auto',
            backgroundColor: 'white',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                backgroundColor: '#fee2e2', color: '#b91c1c',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700
              }}>!</div>
              <div>
                <h3 style={{ margin: 0, color: '#b91c1c' }}>Access denied</h3>
                <p style={{ margin: '6px 0 0 0', color: '#7f1d1d' }}>
                  Only Super Admins can view the Admin panel data. Please log in with a super admin account.
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Navigation Tabs */}
        <div className="admin-page__tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`admin-page__tab${activeTab === tab.key ? ' is-active' : ''}`}
            >
              <span className="admin-page__tab-icon" aria-hidden="true">{tab.icon}</span>
              <span className="admin-page__tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="admin-page__tabs-select">
          <label htmlFor="admin-tab-select" className="admin-page__tabs-select-label">Select section</label>
          <select
            id="admin-tab-select"
            className="admin-page__tabs-select-control"
            value={activeTab}
            onChange={event => setActiveTab(event.target.value)}
          >
            {tabs.map(tab => (
              <option key={tab.key} value={tab.key}>
                {tab.icon} {tab.label}
              </option>
            ))}
          </select>
        </div>

      {/* Report Viewing Modal (Admin) */}
      {showReportModal && viewingReport && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                📑 Payment Report - {new Date(viewingReport.generated_at).toLocaleDateString()}
              </h2>
              <button
                onClick={() => setShowReportModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Expenses</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>{viewingReport.expense_count}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Amount</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                    {formatCurrency(
                      Number(viewingReport.total_amount),
                      // Prefer reimbursement currency from any expense in this report; else derive via helper
                      (reportExpenses[0]?.reimbursement_currency) ||
                      getReportReimbursementCurrency(viewingReport)
                    )}
                  </div>
                </div>
              </div>
            </div>

            {reportExpenses.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Description</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Category</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Original Amount</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Reimbursement</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>ID</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportExpenses.map((expense: any, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{new Date(expense.date).toLocaleDateString()}</td>
                        <td style={{ padding: '8px 12px', color: '#1f2937' }}>{expense.justification}</td>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{expense.type}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#1f2937' }}>
                          {expense.amount?.toFixed(2)} {expense.currency}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', color: '#1f2937' }}>
                          {expense.reimbursement_amount?.toFixed(2)} {expense.reimbursement_currency}
                        </td>
                        <td style={{ padding: '8px 12px', color: '#1f2937', fontFamily: 'monospace' }}>
                          {expense.receipt ? (
                            <a
                              href={fileUrl(`/uploads/${expense.receipt}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#3b82f6', textDecoration: 'none' }}
                            >
                              📷 View
                            </a>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>No receipt</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Excel preview */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <button
                  onClick={() => loadExcelPreview(viewingReport)}
                  disabled={excelLoading}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: excelLoading ? '#d1d5db' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: excelLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {excelLoading ? 'Loading Excel…' : 'Preview Excel (inline)'}
                </button>
                {excelError && <span style={{ color: '#ef4444', fontSize: '12px' }}>{excelError}</span>}
              </div>

              {excelPreviewRows.length > 0 && (
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        {excelPreviewHeaders.map((h, idx) => (
                          <th key={idx} style={{ padding: '8px 10px', textAlign: 'left', color: '#374151' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {excelPreviewRows.map((row, rIdx) => (
                        <tr key={rIdx} style={{ borderTop: '1px solid #e5e7eb' }}>
                          {row.map((cell: any, cIdx: number) => (
                            <td key={cIdx} style={{ padding: '8px 10px', color: '#374151' }}>{String(cell ?? '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                {/* Prefer SharePoint web link for Excel if available; fallback to local report download */}
                {viewingReport.sp_xls_web_url ? (
                  <a
                    href={viewingReport.sp_xls_web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px 16px', backgroundColor: '#10b981', color: 'white', textDecoration: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500'
                    }}
                  >
                    🔗 Open Excel in SharePoint
                  </a>
                ) : (
                  <button
                    onClick={() => handleDownloadExcel(viewingReport)}
                    style={{
                      padding: '8px 16px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                    }}
                  >
                    📥 Download Excel Report
                  </button>
                )}

                {viewingReport.pdf_filename && (
                  <button
                    onClick={() => handleDownloadPdf(viewingReport)}
                    style={{
                      padding: '8px 16px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                    }}
                  >
                    📄 Download PDF Report
                  </button>
                )}

                {/* Remove 'Open ZIP in SharePoint' link; keep folder link if available; always allow ZIP download. */}
                {viewingReport.sp_folder_web_url && (
                  <a
                    href={viewingReport.sp_folder_web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px 16px', backgroundColor: '#374151', color: 'white', textDecoration: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500'
                    }}
                  >
                    📂 Open folder in SharePoint
                  </a>
                )}
                <button
                  onClick={() => handleDownloadReceiptsZip(viewingReport.filename)}
                  style={{
                    padding: '8px 16px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                  }}
                >
                  📦 Download ZIP Receipts
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin-Generated Reset Code Modal */}
      {showResetCodeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 20, maxWidth: 520, width: '92%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Password Reset Code</h3>
              <button onClick={() => setShowResetCodeModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            {resetCodeError ? (
              <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', padding: '8px 10px', borderRadius: 6, marginBottom: 10 }}>{resetCodeError}</div>
            ) : (
              <>
                <p style={{ marginTop: 0, color: '#374151' }}>
                  Share this one-time code with the user securely. It expires in about 1 hour.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 14, color: '#6b7280' }}>User</div>
                  <div style={{ fontWeight: 600 }}>{`${resetCodeData.user?.first_name || ''} ${resetCodeData.user?.last_name || ''}`.trim() || resetCodeData.user?.email}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{resetCodeData.user?.email}</div>
                  <div style={{ height: 1, background: '#e5e7eb', margin: '8px 0' }} />
                  <div style={{ fontSize: 14, color: '#6b7280' }}>Reset Code</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input readOnly value={resetCodeData.code || ''} style={{ flex: 1, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontFamily: 'monospace', fontSize: 18, textAlign: 'center' }} />
                    <button
                      onClick={() => resetCodeData.code && navigator.clipboard?.writeText(resetCodeData.code)}
                      style={{ padding: '8px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                    >Copy</button>
                  </div>
                  {resetCodeData.expiresAt && (
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Expires at: {new Date(resetCodeData.expiresAt).toLocaleString()}</div>
                  )}
                </div>
              </>
            )}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowResetCodeModal(false)} style={{ padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
        {/* Admin Expenses Tab (integrated) */}
        {activeTab === 'expenses' && (
          <div style={{ maxWidth: '1400px' }}>
            <AdminExpensesTab onMessage={showMessage} />
          </div>
        )}

        {activeTab === 'users' && (
          <div style={{ maxWidth: '1400px' }}>
            <div style={{
              backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px'
            }}>
              {/* Create Invite panel */}
              <div style={{ marginBottom: 16, padding: 12, border: '1px dashed #d1d5db', borderRadius: 8, background: '#f9fafb' }}>
                <h4 style={{ margin: '0 0 8px 0' }}>Create Invite</h4>
                <p style={{ margin: '0 0 12px 0', color: '#6b7280' }}>Generate a one-time invite token to allow a user to register.</p>
                {inviteError && (
                  <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', padding: '8px 10px', borderRadius: 6, marginBottom: 10 }}>{inviteError}</div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, minWidth: 240 }}
                  />
                  <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as any)} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                    <option value="team_member">Team Member</option>
                    <option value="partner">Partner</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={inviteDays}
                    onChange={(e) => setInviteDays(parseInt(e.target.value || '14', 10))}
                    style={{ width: 100, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6 }}
                  />
                  <span style={{ color: '#6b7280', fontSize: 12 }}>Expiry days</span>
                  <button onClick={createInvite} disabled={inviteLoading} style={{ padding: '8px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: inviteLoading ? 'not-allowed' : 'pointer' }}>
                    {inviteLoading ? 'Creating…' : 'Create Invite'}
                  </button>
                </div>
                {inviteResult && (
                  <div style={{ marginTop: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                    <div style={{ fontSize: 14, marginBottom: 6 }}>Invite created for <strong>{inviteResult.email}</strong> ({inviteResult.role}).</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input readOnly value={inviteResult.token} style={{ flex: 1, minWidth: 240, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'monospace' }} />
                      <button onClick={() => navigator.clipboard?.writeText(inviteResult.token)} style={{ padding: '6px 10px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Copy token</button>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>Expires at: {new Date(inviteResult.expires_at).toLocaleString()}</div>
                  </div>
                )}

                {inviteHistory.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <h5 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#374151' }}>Existing Invites</h5>
                    <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>Role</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>Token</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>Expires</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inviteHistory.map(inv => {
                            const isUsed = !!inv.used_at;
                            const isExpired = inv.expires_at ? new Date(inv.expires_at) < new Date() : false;
                            return (
                              <tr key={inv.id}>
                                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>{inv.email}</td>
                                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', textTransform: 'capitalize' }}>{inv.role.replace('_', ' ')}</td>
                                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>
                                  <code style={{ fontSize: 12 }}>{inv.token}</code>
                                </td>
                                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>
                                  {inv.expires_at ? new Date(inv.expires_at).toLocaleString() : 'No expiry'}
                                </td>
                                <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>
                                  {isUsed ? (
                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>Used</span>
                                  ) : isExpired ? (
                                    <span style={{ color: '#b91c1c', fontWeight: 600 }}>Expired</span>
                                  ) : (
                                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>Pending</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Users</h3>
                <button
                  onClick={fetchUsers}
                  disabled={usersLoading}
                  style={{ padding: '6px 12px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: usersLoading ? 'not-allowed' : 'pointer' }}
                >
                  {usersLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Role</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Reimb. Currency</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No users</td>
                      </tr>
                    ) : users.map((u) => {
                      const isEditing = editingUserId === u.id;
                      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'â€”';
                      const draft = { ...u, ...editDraft } as User;
                      const superCount = users.filter(x => x.role === 'super_admin').length;
                      const isLastSuper = u.role === 'super_admin' && superCount === 1;
                      return (
                        <tr key={u.id}>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                            {isEditing ? (
                              <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                  value={draft.first_name || ''}
                                  placeholder="First name"
                                  onChange={e => setEditDraft(d => ({ ...d, first_name: e.target.value }))}
                                  style={{ padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 }}
                                />
                                <input
                                  value={draft.last_name || ''}
                                  placeholder="Last name"
                                  onChange={e => setEditDraft(d => ({ ...d, last_name: e.target.value }))}
                                  style={{ padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 }}
                                />
                              </div>
                            ) : (
                              <span>{fullName}</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>{u.email}</td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                            {isEditing ? (
                              <select
                                value={draft.role}
                                onChange={e => setEditDraft(d => ({ ...d, role: e.target.value as any }))}
                                style={{ padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 }}
                              >
                                <option value="super_admin">Super Admin</option>
                                <option value="partner">Partner</option>
                                <option value="team_member">Team Member</option>
                              </select>
                            ) : (
                              <span style={{ textTransform: 'capitalize' }}>{u.role.replace('_', ' ')}</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                            {isEditing ? (
                              <select
                                value={draft.status || 'active'}
                                onChange={e => setEditDraft(d => ({ ...d, status: e.target.value as any }))}
                                style={{ padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 }}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            ) : (
                              <span style={{ textTransform: 'capitalize' }}>{u.status || 'active'}</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                            {isEditing ? (
                              <select
                                value={(draft.reimbursement_currency ?? u.reimbursement_currency ?? 'EUR') as string}
                                onChange={e => setEditDraft(d => ({ ...d, reimbursement_currency: e.target.value }))}
                                style={{ padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, width: 140 }}
                              >
                                <option value="EUR">EUR</option>
                                <option value="USD">USD</option>
                              </select>
                            ) : (
                              <span>{u.reimbursement_currency || 'â€”'}</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                            {isEditing ? (
                              <div style={{ display: 'inline-flex', gap: 8 }}>
                                <button
                                  onClick={() => saveUser(u)}
                                  style={{ padding: '6px 10px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                                >Save</button>
                                <button
                                  onClick={cancelEditUser}
                                  style={{ padding: '6px 10px', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                                >Cancel</button>
                              </div>
                            ) : (
                              <div style={{ display: 'inline-flex', gap: 8 }}>
                                <button
                                  onClick={() => startEditUser(u)}
                                  style={{ padding: '6px 10px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                                >Edit</button>
                                <button
                                  onClick={() => generateResetCode(u)}
                                  disabled={resetCodeLoading}
                                  title="Generate a one-time password reset code for this user"
                                  style={{ padding: '6px 10px', backgroundColor: '#9333ea', color: 'white', border: 'none', borderRadius: 6, cursor: resetCodeLoading ? 'not-allowed' : 'pointer' }}
                                >
                                  {resetCodeLoading ? 'Generating…' : 'Reset Code'}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Other tabs would go here */}
        {activeTab === 'reports' && (
          <div style={{ maxWidth: '1400px' }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Reports</h3>
                {/* User filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ color: '#6b7280', fontSize: '14px' }}>User:</label>
                  <select
                    value={reportUserFilter}
                    onChange={(e) => setReportUserFilter(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                  >
                    <option value="">All users</option>
                    {[...new Set(reports.map(r => r.user_name))].map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>User</th>
                      <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}># Expenses</th>
                      <th style={{ textAlign: 'center', padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No reports found</td>
                      </tr>
                    ) : (
                      filteredReports.map((r) => (
                        <tr key={r.id}>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>{r.user_name}</td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>{formatDate(r.generated_at)}</td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>{formatCurrency(
                            Number(r.total_amount),
                            getReportReimbursementCurrency(r)
                          )}</td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              minWidth: '28px',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              backgroundColor: '#eef2ff',
                              color: '#4f46e5',
                              fontSize: '12px',
                              fontWeight: 600
                            }}>{r.expense_count}</span>
                          </td>
                          <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', gap: '8px' }}>
                              {/* Download XLS (auth required) */}
                              <button
                                onClick={() => handleDownloadExcel(r)}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                ⬇️ XLS
                              </button>
                              {r.pdf_filename && (
                                <button
                                  onClick={() => handleDownloadPdf(r)}
                                  style={{
                                    padding: '6px 10px',
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                >
                                  📄 PDF
                                </button>
                              )}
                              {/* Download Receipts ZIP (auth required) */}
                              <button
                                onClick={() => handleDownloadReceiptsZip(r.filename)}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                📦 ZIP
                              </button>
                              {/* View in modal */}
                              <button
                                onClick={() => openReportModal(r)}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                👁️ View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        

        {activeTab === 'analytics' && (
          <div style={{ maxWidth: '1400px' }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <h3>Analytics Section</h3>
              <p>Analytics functionality will be implemented here.</p>
            </div>
          </div>
        )}
      </div>

      {/* Expense Detail Modal */}
      {selectedExpense && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Expense Details
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>User:</strong> {selectedExpense.user_name} ({selectedExpense.user_email})
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Date:</strong> {formatDate(selectedExpense.date)}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Type:</strong> {selectedExpense.type}
            </div>
            {/* Debug info (expandable) behind toggle */}
            {(() => {
              try {
                const isDebug = (typeof window !== 'undefined') && (
                  (localStorage.getItem('debugAdminExpenses') === '1') ||
                  (new URLSearchParams(window.location.search).get('debug') === '1')
                );
                if (!isDebug) return null;
              } catch { return null; }
              return (
                <details style={{ marginBottom: '12px' }}>
                  <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Debug details</summary>
                  <pre style={{ background: '#f3f4f6', padding: '8px', borderRadius: '6px', overflowX: 'auto' }}>
                    {JSON.stringify(selectedExpense, null, 2)}
                  </pre>
                </details>
              );
            })()}
            <div style={{ marginBottom: '16px' }}>
              <strong>Justification:</strong> {selectedExpense.justification}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Amount:</strong> {formatCurrency(selectedExpense.amount, selectedExpense.currency)}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Reimbursement:</strong> {formatCurrency(selectedExpense.reimbursement_amount, selectedExpense.reimbursement_currency)}
            </div>
            {/* Categorization and VAT details */}
            <div style={{
              marginTop: '8px',
              padding: '12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: '#f9fafb'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Classification:</strong> {selectedExpense.classification || 'N/A'}
              </div>
              {(() => {
                const vatAmount = (selectedExpense.vat_amount ?? null) as number | null;
                if (vatAmount === null || Number.isNaN(vatAmount)) {
                  return (
                    <div><strong>VAT:</strong> N/A</div>
                  );
                }
                // Compute VAT % over total amount in EUR when possible
                let baseInEUR: number | null = null;
                if (selectedExpense.currency === 'EUR') {
                  baseInEUR = selectedExpense.amount;
                } else if (selectedExpense.reimbursement_currency === 'EUR') {
                  baseInEUR = selectedExpense.amount * (selectedExpense.exchange_rate || 0);
                }
                let vatInEUR: number | null = null;
                if (selectedExpense.currency === 'EUR') {
                  vatInEUR = vatAmount;
                } else if (selectedExpense.reimbursement_currency === 'EUR') {
                  vatInEUR = vatAmount * (selectedExpense.exchange_rate || 0);
                }
                const percent = (baseInEUR !== null && vatInEUR !== null && baseInEUR > 0)
                  ? (vatInEUR / baseInEUR) * 100
                  : null;
                return (
                  <div>
                    <strong>VAT:</strong> {formatCurrency(vatAmount, selectedExpense.currency)}
                    {percent !== null ? (
                      <span style={{ color: '#6b7280' }}> ({percent.toFixed(2)}% of total in EUR)</span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}> (EUR % unavailable)</span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setSelectedExpense(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              {selectedExpense.status === 'submitted' && (
                <>
                  <button
                    onClick={() => handleExpenseAction(selectedExpense.id, 'reject')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleExpenseAction(selectedExpense.id, 'approve')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message Display */}
      {message && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: messageType === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          {message}
        </div>
      )}

      {/* Receipt Preview Panel */}
      {showReceiptPreview && (
        <div style={{
          position: 'fixed',
          top: '120px',
          right: '24px',
          width: '50%',
          height: 'calc(100vh - 140px)',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 1000
        }}>
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                Receipt Preview
              </h3>
              {selectedExpenseForPreview && (
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                  {selectedExpenseForPreview.user_name} - {selectedExpenseForPreview.type} - {formatDate(selectedExpenseForPreview.date)}
                </p>
              )}
            </div>
            <button
              onClick={() => {
                setShowReceiptPreview(false);
                setSelectedExpenseForPreview(null);
                setPreviewReceiptUrl('');
              }}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{
            flex: 1,
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto'
          }}>
            {previewReceiptUrl ? (
              previewReceiptUrl.toLowerCase().includes('.pdf') ? (
                <iframe
                  src={previewReceiptUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                  title="Receipt PDF"
                />
              ) : (
                <img
                  src={previewReceiptUrl}
                  alt="Receipt"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              )
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '14px'
              }}>
                No receipt to display
              </div>
            )}
          </div>
          <div style={{
            padding: '16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f8fafc',
            display: 'flex',
            gap: '8px'
          }}>
            <a
              href={previewReceiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🔗 Open in New Tab
            </a>
            <a
              href={previewReceiptUrl}
              download
              style={{
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              💾 Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;

